import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { selectAdminUuid } from '../../features/auth/authSlice';
import { getRoleJSON } from '../utils';
import {
  useListFormSubmissionsQuery,
  useGetRoleAlignmentResponsesQuery,
  useFetchRoleAlignmentResponsesMutation,
  useAskRoleAlignmentMutation,
  useSetFormManagerGatesMutation,
  useNotifyFormManagerMutation,
} from '../../app/api';
import RoleAlignmentForm, { RoleAlignmentResults, roleAlignmentRows } from './RoleAlignmentForm';
import { ManagerialAssessmentResults, MANAGERIAL_ASSESSMENT_SCHEMA } from './ManagerialAssessmentForm';
import ResultActions from './ResultActions';
import FormDashboard from './FormDashboard';

const brand = {
  navy: '#002040', orange: '#F09010', blue: '#0060A0', softGray: '#e9e6e1', charcoal: '#58585b',
};

const FORM_KEY = 'role_alignment';

function TabButton({ active, onClick, icon, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? 'white' : 'transparent',
        color: active ? brand.navy : 'rgba(255,255,255,0.85)',
        border: 'none',
        borderBottom: active ? `3px solid ${brand.orange}` : '3px solid transparent',
        padding: '0.6rem 1.1rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
        borderTopLeftRadius: 8, borderTopRightRadius: 8,
      }}
    >
      <i className={`bi bi-${icon} me-1`} />{children}
    </button>
  );
}
TabButton.propTypes = {
  active: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.string.isRequired,
  children: PropTypes.node,
};

/* Which manager a submission belongs under.
 *
 * The reporting line is read live from company_directory by the API rather than
 * stored on the submission, so this follows the org chart as HR maintains it.
 * Two buckets exist besides the real managers and both are worth seeing rather
 * than hiding: someone at the top of the chart or with no manager recorded, and
 * a submission never linked to a directory row at all (HR fixes that link on
 * Manage Directory).
 */
const MANAGER_ALL = '__all__';
const MANAGER_NONE = '__none__';
const MANAGER_UNLINKED = '__unlinked__';

const managerKeyOf = (sub) => {
  if (!sub.directory_id) return MANAGER_UNLINKED;
  return sub.manager_name || MANAGER_NONE;
};

const managerLabelOf = (sub) => {
  const key = managerKeyOf(sub);
  if (key === MANAGER_UNLINKED) return 'Not in the directory';
  if (key === MANAGER_NONE) return 'No manager set';
  return key;
};

function ManagerFilter({ submissions, value, onChange, shown }) {
  // Built from the submissions on screen, not from the whole directory: a
  // manager with nothing filed yet would only be an empty option.
  const options = React.useMemo(() => {
    const counts = new Map();
    submissions.forEach((s) => {
      const key = managerKeyOf(s);
      const existing = counts.get(key);
      if (existing) existing.count += 1;
      else counts.set(key, { key, label: managerLabelOf(s), count: 1 });
    });
    // Real managers alphabetically; the two catch-all buckets last, whatever
    // their size — they are data-quality rows, not people.
    const isBucket = (k) => k === MANAGER_NONE || k === MANAGER_UNLINKED;
    return [...counts.values()].sort((a, b) => (
      (isBucket(a.key) ? 1 : 0) - (isBucket(b.key) ? 1 : 0)
      || a.label.localeCompare(b.label)
    ));
  }, [submissions]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
      <label style={{ color: brand.navy, fontWeight: 700, fontSize: '0.8rem', margin: 0 }}>
        <i className="bi bi-diagram-3 me-1" style={{ color: brand.blue }} />
        Manager
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          border: `1px solid ${brand.softGray}`, borderRadius: 6, padding: '0.3rem 0.6rem',
          fontSize: '0.82rem', color: brand.navy, background: 'white', minWidth: 220,
        }}
      >
        <option value={MANAGER_ALL}>All managers ({submissions.length})</option>
        {options.map((o) => (
          <option key={o.key} value={o.key}>{o.label} ({o.count})</option>
        ))}
      </select>
      {value !== MANAGER_ALL && (
        <>
          <span style={{ color: brand.charcoal, fontSize: '0.78rem' }}>
            Showing {shown} of {submissions.length}
          </span>
          <button
            type="button"
            onClick={() => onChange(MANAGER_ALL)}
            style={{ background: 'none', border: 'none', color: brand.blue, fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', padding: 0 }}
          >
            Clear
          </button>
        </>
      )}
      <span style={{ color: brand.charcoal, fontSize: '0.72rem', marginLeft: 'auto', opacity: 0.85 }}>
        Direct reports only — reporting lines come from the employee directory.
      </span>
    </div>
  );
}
ManagerFilter.propTypes = {
  submissions: PropTypes.array.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  shown: PropTypes.number.isRequired,
};

/* The two per-person manager gates.
 *
 * They are separate decisions and the order matters: the first asks this
 * person's DIRECT manager to write an assessment of them; the second, and only
 * then, lets that manager read the questionnaire itself. A manager can be asked
 * to assess someone whose candid answers stay shut to them — that is the case
 * these checkboxes exist for, so the read is never implied by the request.
 *
 * Both start off for every submission, including the ones filed before this
 * existed. The server enforces both gates on the manager's read AND write, and
 * forces the read off whenever the request is withdrawn; this component just
 * mirrors that so the boxes never show a state the server would refuse.
 */
function ManagerGates({ sub, gates, onGates, assessed }) {
  const [error, setError] = useState(null);
  const [save, { isLoading }] = useSetFormManagerGatesMutation();
  const [notify, { isLoading: notifying }] = useNotifyFormManagerMutation();
  // Not persisted: this is a receipt for the click, not a record of who has been
  // chased. A record would have to survive a refresh to be worth anything, and
  // that is a column and a migration, not a piece of component state.
  const [notified, setNotified] = useState(null);

  // No directory link means there is no org chart edge to find a manager on, so
  // neither gate can do anything. Say that instead of offering dead checkboxes.
  const unlinked = !sub.directory_id;

  const apply = async (next) => {
    const previous = gates;
    onGates(next);                        // optimistic — these are instant toggles
    setError(null);
    try {
      const res = await save({
        form_key: FORM_KEY, submission_id: sub.id,
        requested: next.requested, can_view: next.canView,
      }).unwrap();
      // The server is the authority on the pair — it forces the read off when
      // the request is off — so the stored state replaces the guess.
      onGates({
        requested: res.manager_assessment_requested,
        canView: res.manager_can_view_responses,
      });
    } catch (e) {
      onGates(previous);
      setError(e?.data?.detail || 'Could not save that. Try again.');
    }
  };

  const sendEmail = async () => {
    setError(null);
    setNotified(null);
    // The mail says "you have been asked to assess this person", which is a
    // strange thing to receive about an assessment already filed. Cheap to
    // check, and the usual cause is a mis-click on the wrong row.
    if (assessed && !window.confirm(
      `${sub.submitter_name || 'This person'} has already been assessed by their manager. Send the request anyway?`)) {
      return;
    }
    try {
      const res = await notify({ form_key: FORM_KEY, submission_id: sub.id }).unwrap();
      setNotified(`Emailed ${res.manager_name || 'their manager'} (${res.manager_email}).`);
    } catch (e) {
      setError(e?.data?.detail || 'Could not send that email. Try again.');
    }
  };

  const box = (checked, disabled, label, hint, onChange) => (
    <label style={{
      display: 'flex', gap: '0.45rem', alignItems: 'flex-start',
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
    }}>
      <input type="checkbox" checked={checked} disabled={disabled || isLoading}
             onChange={(e) => onChange(e.target.checked)}
             style={{ marginTop: '0.15rem', accentColor: brand.blue, cursor: 'inherit' }} />
      <span>
        <span style={{ fontWeight: 700, color: brand.navy, fontSize: '0.78rem' }}>{label}</span>
        <span style={{ display: 'block', color: brand.charcoal, fontSize: '0.72rem' }}>{hint}</span>
      </span>
    </label>
  );

  return (
    <div style={{ padding: '0.6rem 1rem', background: '#fbfaf8', borderTop: '1px solid #f0ece5' }}>
      {unlinked ? (
        <div style={{ fontSize: '0.75rem', color: brand.charcoal }}>
          <i className="bi bi-info-circle me-1" />
          This submission is not linked to a directory entry, so there is no manager to ask.
          HR can fix the link on Manage Directory.
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '1.75rem', flexWrap: 'wrap' }}>
          {box(gates.requested, false,
            'Ask their manager for an assessment',
            'Their direct manager is asked to complete a Managerial Assessment of them.',
            (checked) => apply({ requested: checked, canView: checked && gates.canView }))}
          {box(gates.canView, !gates.requested,
            'Let their manager read these answers',
            gates.requested
              ? 'Their manager sees this questionnaire alongside the assessment form.'
              : 'Available once an assessment has been requested.',
            (checked) => apply({ requested: gates.requested, canView: checked }))}
        </div>
      )}

      {/* Sending is a separate click from ticking the box: the gate is
          bookkeeping HR may do in a batch weeks before anyone should be chased,
          and the same button sends a reminder later. Only offered once the
          request is on, because the link goes to a page that stays shut until
          then - the server refuses it too. */}
      {!unlinked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem',
                      flexWrap: 'wrap', marginTop: '0.55rem' }}>
          <button
            type="button"
            onClick={sendEmail}
            disabled={!gates.requested || notifying}
            title={gates.requested
              ? `Email ${managerLabelOf(sub)} that this assessment is waiting`
              : 'Ask their manager for an assessment first'}
            style={{
              background: 'white', border: '1px solid #cdd5dd', color: brand.navy,
              borderRadius: 6, padding: '0.25rem 0.6rem', fontSize: '0.74rem',
              fontWeight: 700, cursor: gates.requested ? 'pointer' : 'default',
              opacity: gates.requested ? 1 : 0.5,
            }}
          >
            <i className="bi bi-envelope me-1" />
            {notifying ? 'Sending…' : 'Email their manager'}
          </button>
          <span style={{ color: brand.charcoal, fontSize: '0.72rem' }}>
            Names the employee and links to their manager&apos;s Team Role Alignment page.
          </span>
        </div>
      )}

      {notified && (
        <div style={{ color: '#2E7D32', fontSize: '0.74rem', marginTop: '0.4rem' }}>
          <i className="bi bi-check-circle me-1" />{notified}
        </div>
      )}
      {error && (
        <div style={{ color: '#c0392b', fontSize: '0.74rem', marginTop: '0.4rem' }}>
          <i className="bi bi-exclamation-triangle me-1" />{error}
        </div>
      )}
    </div>
  );
}
ManagerGates.propTypes = {
  sub: PropTypes.object.isRequired,
  gates: PropTypes.object.isRequired,
  onGates: PropTypes.func.isRequired,
  assessed: PropTypes.bool,
};

/* One person's answers, fetched when a reviewer expands their row.
 *
 * The list never carries answers — TECH reads it too — so they come from their
 * own CONFIDENTIAL_REVIEWER endpoint, which logs the read. Mounted only while
 * the row is open, and the query keeps nothing once it unmounts, so collapsing
 * a row lets go of the answers and re-opening it is a fresh (logged) read.
 */
function ResponsesPanel({ sub }) {
  const { data, isLoading, isError, error } = useGetRoleAlignmentResponsesQuery({ submission_id: sub.id });
  if (isLoading) {
    return <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary" role="status" /></div>;
  }
  if (isError || !data) {
    return (
      <div style={{ color: '#c0392b', fontSize: '0.82rem', padding: '0.5rem 0' }}>
        <i className="bi bi-exclamation-triangle me-1" />
        {error?.data?.detail || 'Could not load these answers.'}
      </div>
    );
  }
  const { assessment } = data;
  return (
    <>
      <ResultActions name={sub.submitter_name || sub.submitter_email}
        submission={data} assessment={assessment} />
      {/* Named on the printout: on screen the person is in the row above,
          which the print rules hide. */}
      <div style={{ fontWeight: 800, color: brand.navy, marginBottom: '0.5rem' }}>
        Role Alignment — {sub.submitter_name || sub.submitter_email || 'Unknown'}
      </div>
      <RoleAlignmentResults responses={data.responses} />

      <div style={{ marginTop: '1.5rem', borderTop: `2px solid ${brand.orange}`, paddingTop: '1rem' }}>
        <div style={{ fontWeight: 800, color: brand.navy, fontSize: '0.95rem', marginBottom: '0.5rem' }}>
          Managerial Assessment
        </div>
        {assessment ? (
          <>
            <div style={{ fontSize: '0.78rem', color: brand.charcoal, marginBottom: '0.75rem' }}>
              By {assessment.submitter_name || 'their manager'}
              {assessment.created ? ` — ${new Date(assessment.created).toLocaleString()}` : ''}
            </div>
            <ManagerialAssessmentResults responses={assessment.responses} />
          </>
        ) : (
          <div style={{ fontSize: '0.85rem', color: brand.charcoal }}>
            {sub.directory_id
              ? 'Their direct manager has not filed an assessment yet. It appears on their Team Role Alignment page as due.'
              : 'This person is not linked to a directory entry, so no manager assessment can be matched to them. HR can fix the link on Manage Directory.'}
          </div>
        )}
      </div>
    </>
  );
}
ResponsesPanel.propTypes = { sub: PropTypes.object.isRequired };

// One submission. What a row offers depends on the viewer, and the two roles
// are separate jobs:
//   TECH                   runs the process — the manager checkboxes and the
//                          chasing email. Never sees the answers.
//   CONFIDENTIAL_REVIEWER  expands the row to read the answers and the manager's
//                          assessment of that person. Changes nothing.
// Someone holding both gets both. The server enforces each half on its own.
function SubmissionRow({ sub, assessment, canManage, canRead }) {
  const [open, setOpen] = useState(false);
  // Held here rather than inside the checkboxes so the header badge tracks the
  // toggle immediately — the submissions list has no cache invalidation to
  // refetch behind it (tagTypes are inert in this slice).
  const [gates, setGates] = useState({
    requested: Boolean(sub.manager_assessment_requested),
    canView: Boolean(sub.manager_can_view_responses),
  });
  return (
    <div style={{ border: '1px solid #e2ddd4', borderRadius: 10, marginBottom: '0.75rem', background: 'white', overflow: 'hidden' }}>
      <button
        type="button"
        onClick={canRead ? () => setOpen((o) => !o) : undefined}
        disabled={!canRead}
        style={{ width: '100%', textAlign: 'left', background: 'white', border: 'none', padding: '0.85rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: canRead ? 'pointer' : 'default', color: 'inherit' }}
      >
        <span>
          <span style={{ fontWeight: 700, color: brand.navy }}>{sub.submitter_name || 'Unknown'}</span>
          {sub.submitter_email && <span style={{ color: brand.charcoal, fontSize: '0.8rem', marginLeft: '0.5rem' }}>{sub.submitter_email}</span>}
          {/* Straight from the directory, so it tracks the current org chart
              rather than whatever was true when the form was filed. */}
          <span style={{ display: 'block', color: brand.charcoal, fontSize: '0.74rem', marginTop: '0.1rem' }}>
            {sub.submitter_title && <>{sub.submitter_title} · </>}
            <span style={{ opacity: 0.85 }}>Reports to </span>
            <span style={{ fontWeight: 600 }}>{managerLabelOf(sub)}</span>
            {sub.submitter_is_active === false && (
              <span style={{ marginLeft: '0.5rem', color: '#8a5a00', fontWeight: 700 }}>· Terminated</span>
            )}
          </span>
        </span>
        <span style={{ color: brand.charcoal, fontSize: '0.78rem' }}>
          {assessment ? (
            <span style={{ background: '#2E7D32', color: 'white', borderRadius: 3, padding: '1px 7px',
                           fontSize: '0.68rem', fontWeight: 700, marginRight: '0.6rem' }}>
              Manager assessed
            </span>
          ) : (
            <span style={{ background: '#FDF0E0', color: '#8a5a00', border: '1px solid #f0d9b5',
                           borderRadius: 3, padding: '1px 7px', fontSize: '0.68rem',
                           fontWeight: 700, marginRight: '0.6rem' }}>
              {gates.requested ? 'Awaiting manager' : 'Manager not asked'}
            </span>
          )}
          {sub.created ? new Date(sub.created).toLocaleString() : ''}
          {canRead && <i className={`bi bi-chevron-${open ? 'down' : 'right'} ms-2`} />}
        </span>
      </button>

      {/* Outside the expander on purpose: these are the controls someone works
          down the list flipping, and making them expand each row first would
          turn a one-pass job into forty clicks. TECH only — a reviewer reads,
          and the server refuses the write from anyone else. */}
      {canManage && (
        <ManagerGates sub={sub} gates={gates} onGates={setGates} assessed={Boolean(assessment)} />
      )}

      {canRead && open && (
        <div className="printable-result" style={{ padding: '0.5rem 1rem 1rem', borderTop: '1px solid #eee' }}>
          <ResponsesPanel sub={sub} />
        </div>
      )}
    </div>
  );
}
SubmissionRow.propTypes = {
  sub: PropTypes.object.isRequired,
  assessment: PropTypes.object,
  canManage: PropTypes.bool,
  canRead: PropTypes.bool,
};

/* Ask a question across every submitted form. CONFIDENTIAL_REVIEWER only.
 *
 * THE CORPUS IS BUILT HERE AND SENT WITH THE QUESTION, which looks odd until you
 * know why: the server stores `responses` as opaque JSON and has no copy of the
 * form schema, so it cannot turn `co_pct` or `hats_shed` back into a question.
 * `roleAlignmentRows` — the same function that renders the results below — is
 * the only place that knows. Sending its output means the model reads exactly
 * what a reviewer would read, and there is no second copy of the schema to drift.
 *
 * The list on this page carries no answers, so they are fetched at the moment
 * of asking — for the filtered set only — and dropped again afterwards. That
 * fetch is logged as one batch read; the server also caps the payload,
 * rate-limits per person, and records the question asked.
 */
function AskPanel({ submissions }) {
  const [question, setQuestion] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [fetchResponses, { isLoading: fetching }] = useFetchRoleAlignmentResponsesMutation();
  const [ask, { isLoading: asking }] = useAskRoleAlignmentMutation();
  const isLoading = fetching || asking;

  const submit = async () => {
    setError(null);
    setResult(null);
    try {
      const { submissions: withAnswers } = await fetchResponses({
        submission_ids: submissions.map((s) => s.id),
      }).unwrap();
      const corpus = withAnswers.map((s) => ({
        name: s.submitter_name || s.submitter_email || 'Unknown',
        submitted: s.created ? new Date(s.created).toLocaleDateString() : null,
        rows: roleAlignmentRows(s.responses).map((r) => ({ question: r.question, answer: r.answer })),
        assessment_rows: s.assessment
          ? roleAlignmentRows(s.assessment.responses, MANAGERIAL_ASSESSMENT_SCHEMA)
            .map((r) => ({ question: r.question, answer: r.answer }))
          : [],
      }));
      setResult(await ask({ question: question.trim(), submissions: corpus }).unwrap());
    } catch (e) {
      // 404 means an admin hasn't switched the feature on — say that rather
      // than "not found", which reads like the page is broken.
      setError(e?.status === 404
        ? 'Ask is switched off. An admin can enable it in Super Admin → Config.'
        : (e?.data?.detail || 'That question could not be answered.'));
    }
  };

  return (
    <div style={{ border: `1px solid ${brand.softGray}`, borderRadius: 10, padding: '1rem', marginBottom: '1.25rem', background: '#fbfaf8' }}>
      <div style={{ fontWeight: 800, color: brand.navy, fontSize: '0.92rem', marginBottom: '0.15rem' }}>
        <i className="bi bi-stars me-2" style={{ color: brand.orange }} />
        Ask about these submissions
      </div>
      <div style={{ color: brand.charcoal, fontSize: '0.78rem', marginBottom: '0.6rem' }}>
        Answered from the {submissions.length} submitted form{submissions.length === 1 ? '' : 's'} on
        this page and nothing else. 
      </div>
      <textarea
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder="e.g. Who wants to move off the Operations path, and what did they say about why?"
        rows={2}
        style={{
          width: '100%', border: `1px solid ${brand.softGray}`, borderRadius: 6,
          padding: '0.5rem 0.7rem', fontSize: '0.85rem', color: brand.navy,
          fontFamily: 'inherit', resize: 'vertical',
        }}
      />
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={submit}
          disabled={isLoading || !question.trim()}
          style={{
            background: isLoading || !question.trim() ? brand.softGray : brand.navy,
            color: isLoading || !question.trim() ? brand.charcoal : 'white',
            border: 'none', borderRadius: 6, padding: '0.4rem 1rem',
            fontWeight: 700, fontSize: '0.82rem',
            cursor: isLoading || !question.trim() ? 'default' : 'pointer',
          }}
        >
          {isLoading ? 'Reading the forms…' : 'Ask'}
        </button>
        {result && (
          <button type="button" onClick={() => { setResult(null); setQuestion(''); }}
            style={{ background: 'none', border: 'none', color: brand.blue, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
            Clear
          </button>
        )}
      </div>

      {error && (
        <div style={{ marginTop: '0.7rem', background: 'rgba(192,57,43,0.08)', border: '1px solid #c0392b', color: '#c0392b', borderRadius: 6, padding: '0.5rem 0.75rem', fontSize: '0.82rem' }}>
          <i className="bi bi-exclamation-triangle me-1" />{error}
        </div>
      )}

      {result && (
        <div style={{ marginTop: '0.75rem', background: 'white', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.85rem 1rem' }}>
          {/* Flagged rather than buried in the prose: "the forms don't say" is a
              different kind of answer from an answer, and the reader should not
              have to infer which one they got. */}
          {result.insufficient && (
            <div style={{ color: '#8a5a00', background: '#FDF0E0', border: '1px solid #f0d9b5', borderRadius: 5, padding: '0.35rem 0.6rem', fontSize: '0.78rem', fontWeight: 700, marginBottom: '0.5rem' }}>
              <i className="bi bi-info-circle me-1" />The forms don&apos;t fully answer this
            </div>
          )}
          <div style={{ color: brand.navy, fontSize: '0.88rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {result.answer}
          </div>
          {(result.people_referenced || []).length > 0 && (
            <div style={{ marginTop: '0.6rem', paddingTop: '0.5rem', borderTop: `1px solid ${brand.softGray}`, fontSize: '0.76rem', color: brand.charcoal }}>
              Drawn from: {result.people_referenced.join(', ')}
            </div>
          )}
          <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: brand.charcoal, opacity: 0.8 }}>
            Read the submissions below before acting on this — it is a summary of what people wrote,
            not a substitute for it.
          </div>
        </div>
      )}
    </div>
  );
}
AskPanel.propTypes = { submissions: PropTypes.array };

// Tech → Forms → Role Alignment. Three tabs: how far completion has got, the
// list of submitted responses, and a preview of the live form. Open to TECH
// (runs the process, never sees answers) and CONFIDENTIAL_REVIEWER (reads the
// answers, changes nothing); see SubmissionRow for who gets what.
export default function TechRoleAlignment() {
  const [tab, setTab] = useState('dashboard');
  // Only decides what to OFFER — every endpoint enforces its own role. Starts
  // closed so nothing flashes on screen that the viewer can't use.
  const currentUserUuid = useSelector(selectAdminUuid);
  const [access, setAccess] = useState({ canManage: false, canRead: false });
  useEffect(() => {
    let cancelled = false;
    if (currentUserUuid) {
      getRoleJSON(currentUserUuid).then((rj) => {
        if (!cancelled) setAccess({ canManage: Boolean(rj?.isTech), canRead: Boolean(rj?.isConfidentialReviewer) });
      });
    }
    return () => { cancelled = true; };
  }, [currentUserUuid]);
  const { data, isLoading, isError } = useListFormSubmissionsQuery({ form_key: FORM_KEY }, { skip: tab !== 'results' });
  // Assessments are a separate form_key; paired here on the employee's directory
  // id (the questionnaire's author) ↔ subject_directory_id (who it is about).
  const { data: asmtData } = useListFormSubmissionsQuery(
    { form_key: 'managerial_assessment' }, { skip: tab !== 'results' });
  const assessmentBySubject = React.useMemo(() => {
    const m = {};
    (asmtData?.submissions || []).forEach((a) => {
      // Newest wins: submissions come back newest-first, so only take the first.
      if (a.subject_directory_id && !m[a.subject_directory_id]) m[a.subject_directory_id] = a;
    });
    return m;
  }, [asmtData]);
  const submissions = data?.submissions || [];
  const [manager, setManager] = useState(MANAGER_ALL);
  const visible = React.useMemo(
    () => (manager === MANAGER_ALL
      ? submissions
      : submissions.filter((s) => managerKeyOf(s) === manager)),
    [submissions, manager],
  );

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>
      <div style={{ background: brand.navy, borderRadius: '12px 12px 0 0', padding: '1.25rem 1.5rem 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.orange }} />
        <p style={{ color: brand.orange, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 2px' }}>
          Tech · Forms
        </p>
        <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 0.75rem' }}>Role Alignment</h3>
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <TabButton active={tab === 'dashboard'} onClick={() => setTab('dashboard')} icon="speedometer2">
            Dashboard
          </TabButton>
          <TabButton active={tab === 'results'} onClick={() => setTab('results')} icon="clipboard-data">
            Results{submissions.length ? ` (${submissions.length})` : ''}
          </TabButton>
          <TabButton active={tab === 'form'} onClick={() => setTab('form')} icon="file-text">Form</TabButton>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '0 0 12px 12px', padding: '1.5rem', boxShadow: '0 2px 12px rgba(10,41,71,0.1)' }}>
        {tab === 'dashboard' ? (
          <FormDashboard formKey={FORM_KEY} />
        ) : tab === 'form' ? (
          <RoleAlignmentForm mode="preview" />
        ) : isLoading ? (
          <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
        ) : isError ? (
          <div style={{ textAlign: 'center', color: brand.charcoal, padding: '2rem' }}>Unable to load submissions.</div>
        ) : submissions.length === 0 ? (
          <div style={{ textAlign: 'center', color: brand.charcoal, padding: '2rem' }}>No submissions yet.</div>
        ) : (
          <>
          <ManagerFilter submissions={submissions} value={manager} onChange={setManager}
                         shown={visible.length} />
          {/* Ask reads the FILTERED set, so "answered from the N forms on this
              page" stays true and a question can be scoped to one manager's
              team by narrowing the list first. */}
          {access.canRead ? (
            <AskPanel submissions={visible} />
          ) : (
            <div style={{ color: brand.charcoal, fontSize: '0.78rem', marginBottom: '1rem' }}>
              <i className="bi bi-lock me-1" />
              Answers and manager assessments are visible only to Confidential Form Reviewers.
            </div>
          )}
          {visible.length === 0 ? (
            <div style={{ textAlign: 'center', color: brand.charcoal, padding: '2rem' }}>
              No submissions from that manager&apos;s reports.
            </div>
          ) : visible.map((s) => (
            <SubmissionRow key={s.id} sub={s} canManage={access.canManage} canRead={access.canRead}
                           assessment={s.directory_id ? assessmentBySubject[s.directory_id] : null} />
          ))}
          </>
        )}
      </div>
    </div>
  );
}
